import React, {Component} from "react";
import CourierDeliveryInstructions from "./CourierDeliveryInstructions";
import ShippingQuote from "./ShippingQuote";
import {connect} from "react-redux";
import {graphql} from "react-apollo";
import WarningBanner from "../../components/WarningBanner";
import Loading from "../../components/Loading";
import GraphQLErrors from "../../components/GraphQLErrors";
import {fromJS, List, Map} from "immutable";
import {GET_ORDER, ON_ORDER_UPDATED} from "../../queries/shipping";
import PackagesList from "./PackagesList";
import TrackingList from "./TrackingList";
import AddressInline from "../../components/AddressInline";
import {Link} from "react-router";
import "./OrderDetails.scss";


class OrderDetails extends Component {

  componentDidMount() {
    if (this.props.orderId && !this.unsubscribe) {
      this.subscribe(this.props.orderId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.orderId && (prevProps.orderId !== this.props.orderId)) {
      this.subscribe(this.props.orderId);
    }
  }

  subscribe(orderId) {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.unsubscribe = this.props.subscribeToUpdates(this.props, {});
  }


  render() {
    let {loading, error} = this.props.data;
    if (loading) return <Loading />
    if (error) return <GraphQLErrors error={error} />        
    const order = fromJS(this.props.data.shippingOrder);

    let shippingQuote = null;
    let shippingQuoteLabel = "Shipping Quote (n/a)";
    if (order.get("quoteStatus") !== "unavailable") {
      shippingQuote = <ShippingQuote orderId={this.props.orderId} />
      shippingQuoteLabel = "Shipping Quote";
    }
    if (order.get("fulfillmentStatus") !== "preparation") {
      shippingQuoteLabel = "Shipment";
    }

    return (
      <div className="OrderDetails">
        <Warnings order={order} />
        <div className="addresses section">
          <div>
            <h4>Ship to</h4>
            <AddressInline address={order.get("deliveryAddress", Map())} inline={false} />
          </div>
          <div>
            <h4>Delivery Instructions</h4>
            <CourierDeliveryInstructions
              courierId={order.get("courierId")}
              instructions={order.get("courierDeliveryInstructions")} />
          </div>
        </div>
        {this.isTracked(order) && this.renderTrackingList(order)}
        {this.mayNotBeTracked(order) && this.renderPackages(order)}
        <div className="section">
          <h4>{shippingQuoteLabel}</h4>
          {shippingQuote}
        </div>
      </div>
    )
  }

  isTracked(order) {
    // Having a replacement overrides the rest
    if (order.get("hasReplacement", false)) {
      return true;
    }
    
    return List(["dispatch", "in_transit", "delivered", "partially_delivered", "voided", "failed"]).includes(order.get("fulfillmentStatus"));
  }
  mayNotBeTracked(order) {
    // When status is "dispatch", the tracking might yet not be active.
    return !this.isTracked(order) || order.get("fulfillmentStatus") === "dispatch";
  }

  renderTrackingList(order) {

    let extraInfo = order.get("transportMethod") === "cargo" ? "(delivery via Cargo)" : "";
    return (
      <div className="tracking section">
        <h4>Parcels {extraInfo}</h4>
        <TrackingList
          courierId={order.get("courierId")}
          orderId={order.get("orderId")}
          packages={order.get("packages")}
          fulfillmentStatus={order.get("fulfillmentStatus")}
          replacedPackageIds={order.get("replacedPackageIds")}
        />
      </div>
    )
  }

  renderPackages(order) {
    let stockStatus = "";
    switch (order.get("stockStatus")) {
      case "reserved":
        stockStatus = "(stock reserved)";
        break;
      case "not_reserved":
        stockStatus = "(stock not reserved)";
        break;
      case "requested":
        stockStatus = "(reserving stock...)";
        break;
      case "failed":
        stockStatus = "(failed stock reservation)";
        break;
      default:
        // do nothing.
    }

    return (
      <div className="packages section">
        <h4>Packages {stockStatus}</h4>
        <PackagesList
          courierId={order.get("courierId")}
          packages={order.get("packages")}
          orderId={order.get("orderId")}
          fulfillmentStatus={order.get("fulfillmentStatus")}
          />
      </div>
    );
  }

}

function Warnings(props) {
  const { order } = props;
  
  function getTrackingFromId(id) {
    const packages = order.get("packages");
    const pack = packages && packages.find((p) => p.get("packageId") === id);
    const trackingNr = pack && pack.get("trackingNr");

    return trackingNr || id;
  }

  return (
    <>
      <WarningBanner show={order.get("pickupOverdue")} message="Pickup for this shipment is overdue. View the order log for more info." />
      <WarningBanner show={order.get("hold") === true} message="This order has been marked as ON HOLD, and will not start automatically. View shipping log for more information." />
      <WarningBanner show={order.get("type") === "complimentary"} error={false} message="This shipment was marked as complimentary." />
      <WarningBanner show={order.get("notes") !== ""} error={false} message={`Note: ${order.get("notes")}`} />
      <WarningBanner show={order.get("refSalesOrderId") !== ""} error={false} message={
        <>
          This shipment has a reference to Sales Order <Link to={`/sales/orders/${order.get("refSalesOrderId")}`}>{order.get("refSalesOrderId")}</Link>.
        </>
      } />
      <WarningBanner show={order.get("type") === "replacement"} error={false} message={
        <>
          Replacement order for <Link to={`/shipping/orders/${order.get("originalOrderId")}`}>{order.get("originalOrderId")}</Link>.
        </>
      } />
      <WarningBanner show={order.get("hasReplacement")} error={false} message={
        <>
          This shipment has a replacement order:
          {order.get("replacedPackageIds").map((r) => {
            const packageId = r.get("packageId");
            const orderId = r.get("replacementOrderId");
            return (
              <span key={packageId}>
                <br />For package "{getTrackingFromId(packageId)}": <Link to={`/shipping/orders/${orderId}`}>{orderId}</Link>
              </span>
            );
          })}
        </>
      } />
    </>
  );
}

const mapDispatchToProps = dispatch => ({
  subscribeToUpdates: (props, params) => {
    return props.data.subscribeToMore({
      document: ON_ORDER_UPDATED,
      variables: {
        orderId: props.orderId
      },
      updateQuery: (prev, { subscriptionData }) => {
        props.data.refetch();
        return prev;
      }
    })
  }
})

const withSubscription = connect(null, mapDispatchToProps);

const withQuery = graphql(GET_ORDER, {
  options: (props) => ({
    fetchPolicy: "network-only",
    variables: {
      orderId: props.orderId
    }
  })
})


export default withQuery(withSubscription(OrderDetails));